import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ChangeDetectionStrategy,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import deepmerge from 'deepmerge';
import { GroupBase } from '../../../models/blueprint.model';
import {
  RecordUpdateEmission,
  ReportInfo,
  ReportOptions,
  ReportRecord,
  ReportView,
  TemplateProjectionMap,
} from '../../../models/report.model';
import { TemplateField } from '../../../models/template.model';
import {
  CONFIDENTIALITY_FIELD_ID,
  TOTAL_SIZE_FIELD_ID,
  NON_EDITABLE_FIELD_IDS,
} from '../../../shared/templates/config/common';
import { EditableFieldsService } from '../../editable-fields.service';
import { onDrop } from '../../summary-view/summary-view.helper';
import { entitiesWithConfidentiality } from '../../../shared/constants/entity.const';
import { NgClass, NgTemplateOutlet, AsyncPipe, TitleCasePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { PrimeTemplate } from 'primeng/api';
import { MatIcon } from '@angular/material/icon';

import { HighlightDatePipe } from './table-info.pipe';
import { ReportFieldFormatterPipe } from '../../../shared/pipes/report-field-formatter.pipe';
import { SelectTemplateOutletPipe } from '../../../shared/pipes/select-template-outlet.pipe';
import { ReportRecordRouteFnPipe } from '../../../shared/pipes/report-record-route-fn.pipe';
import { L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rep-table-info',
  templateUrl: './table-info.component.html',
  styleUrl: './table-info.component.scss',
  imports: [
    TableModule,
    PrimeTemplate,
    NgClass,
    MatIcon,
    NgTemplateOutlet,
    AsyncPipe,
    TitleCasePipe,
    HighlightDatePipe,
    ReportFieldFormatterPipe,
    SelectTemplateOutletPipe,
    ReportRecordRouteFnPipe,
    L10nTranslatePipe
],
})
export class TableInfoComponent implements OnInit, OnDestroy {
  public editableFieldsService = inject(EditableFieldsService);
  private readonly translationService = inject(L10nTranslationService);

  readonly confidentialityTmpl = input<TemplateRef<any>>();
  readonly isPDF = input<boolean>(false);
  readonly reportData = input.required<ReportInfo>();
  readonly currentView = input.required<ReportView>();
  readonly groupsToDisplay = input.required<GroupBase<TemplateField>[]>();
  readonly headerGroups = input.required<GroupBase<TemplateField>[]>();
  readonly templateProjectionMap = input.required<TemplateProjectionMap>();
  readonly reportLocale = input.required<number>();
  readonly reportOptions = input.required<ReportOptions>();
  readonly reportUpdateClick = output<RecordUpdateEmission>();
  readonly removeRecord = output<any>();
  readonly reportReordered = output<ReportRecord[]>();

  reportRecordData: ReportRecord[];
  allFields: TemplateField[];
  headerFields: TemplateField[];
  totalSizeField: TemplateField;
  totalSize = '';
  confidentialityId = CONFIDENTIALITY_FIELD_ID;
  draggedRowIndex: number;
  nonEditableFieldIds = NON_EDITABLE_FIELD_IDS;
  isEntityWithConfidentiality: boolean;

  constructor() {
    effect(() => {
      const reportData = this.reportData();
      const currentView = this.currentView();
      const groupsToDisplay = this.groupsToDisplay();
      const headerGroups = this.headerGroups();

      this.editableFieldsService.mapEditableFields(groupsToDisplay);

      this.reportRecordData = deepmerge([], reportData ? reportData.records : null);
      this.allFields = [].concat(...(groupsToDisplay || []).map(group => group.fields));
      this.allFields.forEach(field => {
        field.label = field.label.replace('\\n', ' \n');
      });
      this.headerFields = []
        .concat(...(headerGroups || []).map(group => group.fields))
        .sort((a, b) => a.options.headerSortOrder - b.options.headerSortOrder);
      this.generateTotalSizeField();
      this.isEntityWithConfidentiality = !!entitiesWithConfidentiality[reportData.entity];
    });
  }

  get currentTotalLabel(): string {
    return this.translationService.translate('recordPPT.currentTotal', {
      recordLength: this.reportRecordData?.length ?? 0,
    });
  }

  ngOnInit() {
    /* required by angular-l10n */
  }

  ngOnDestroy(): void {
    /* required by angular-l10n */
  }

  emitEditableFields(event: MouseEvent, id: number, data: any) {
    const emittedObject = this.editableFieldsService.generateRecordUpdateEmission(event, id, data);
    if (emittedObject != null && !this.nonEditableFieldIds.includes(id)) {
      emittedObject.locale = this.reportLocale();
      this.reportUpdateClick.emit(emittedObject);
    }
  }

  generateTotalSizeField() {
    if (this.headerFields && this.headerFields.length > 0) {
      const totalSizeField = this.headerFields.find(a => a.id === TOTAL_SIZE_FIELD_ID[this.reportData().entity]);
      if (totalSizeField) {
        this.totalSizeField = totalSizeField;
      }
    }
  }

  dragStartColumn(startIndex: number) {
    this.draggedRowIndex = startIndex;
  }

  dropColumn(droppedIndex: number) {
    if (this.draggedRowIndex != droppedIndex) {
      onDrop(
        { previousIndex: this.draggedRowIndex, currentIndex: droppedIndex } as CdkDragDrop<
          Array<{ [key: string]: any }>
        >,
        this.reportData().records,
        this.reportReordered
      );
    }
  }
}
